import { Component, OnInit } from '@angular/core';
import { mergeMap, finalize } from 'rxjs/operators';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import {
  AbstractBlModal,
  ModalSize,
  ModalButtonType,
  ModalButtonColor,
  ModalButton,
  ModalReason,
  BlModalService,
  BlDialogService,
} from '@blcloud/bl-ng-ui-component';
import { EmployeeGuideComponent, IMaintenanceOrganization, WithAllCompanyDiv } from '@blcloud/bl-ng-share-module';
import { AsyncSubject } from 'rxjs/AsyncSubject';
import { IEmployee, IContactInitDisplaySettingDtl } from '@blcloud/bl-datamodel';
import { ContactInitSettingModalService } from './contact-init-setting-modal.service';
import {
  isEmpty as _isEmpty, cloneDeep as _cloneDeep, filter as _filter, isNil as _isNil, forEach as _forEach, find as _find, reject as _reject,
  includes as _includes
} from 'lodash';
import { ApplyPeriodCriteriaDivArray, ApplyPeriodCriteriaDiv } from '@blcloud/bl-datamodel/enum/bizcmn/apply-period-criteria-div';
import { MonthWeekDayDivArray, MonthWeekDayDiv } from '@blcloud/bl-datamodel/enum/bizcmn/month-week-day-div';
import * as moment from 'moment';
import { IContactInitDisplaySettingEx } from './contact-init-setting-modal.interface';

/**
 * コンタクト初期表示色設定コンポーネント
 */
@Component({
  templateUrl: './contact-init-setting-modal.component.html',
  styleUrls: ['./contact-init-setting-modal.component.scss'],
  providers: [ContactInitSettingModalService]
})
export class ContactInitSettingModalComponent extends AbstractBlModal implements OnInit {

  /** 画面タイトル */
  readonly headerTitle = 'コンタクト初期表示設定';

  /** コンタクト初期表示設定 */
  contactInitDisplaySettingEx: IContactInitDisplaySettingEx = {};
  /** コンタクト初期表示設定詳細(一覧) */
  contactInitDisplaySettingDtlGrid: IContactInitDisplaySettingDtl = {};
  /** コンタクト初期表示設定詳細(カレンダー) */
  contactInitDisplaySettingDtlCalendar: IContactInitDisplaySettingDtl = {};

  /** 実施内容ごとに選択するチェックボックス選択(カレンダー) */
  isCalendarSelectIndividually = false;
  /** コンタクトグループ区分チェックボックス選択(カレンダー) */
  isCalendarSelectContactCheckDivArray = false;
  /** 実施内容ごとに選択するチェックボックス選択(一覧) */
  isGridSelectIndividually = false;
  /** コンタクトグループ区分チェックボックス選択(一覧) */
  isGridSelectContactCheckDivArray = false;

  /**
   * ログイン組織通知イベント
   */
  loginOrganizationSubject: AsyncSubject<IMaintenanceOrganization> = new AsyncSubject();

  /** 組織情報表示区分 */
  WithAllCompanyDiv = WithAllCompanyDiv;

  /** ログイン担当者従業員コード */
  loginUserEmpCode: string;
  /** ログイン担当者従業員名称 */
  loginUserEmpName: string;

  /** 従業員使用非活性フラグ（カレンダー） */
  employeeDisableFlgCalendar = false;
  /** 担当者名（カレンダー） */
  calendarPicName: string;

  /** ログイン担当者使用フラグ（カレンダー） */
  isUseLoginPicFlgCalendar = false;
  /** ログイン担当者使用フラグ（一覧） */
  isUseLoginPicFlgGrid = false;

  /** 従業員使用非活性フラグ（コンタクト一覧） */
  employeeDisableFlgContactList = false;
  /** 担当者名（カレンダー） */
  contactListPicName: string;

  /** コンタクトグループ区分 */
  contactCheckDivArray: { code: number, name: string }[] = [
    { code: 1, name: '車検' }, { code: 2, name: '点検' }, { code: 3, name: 'その他' }
  ];

  /** コンタクト実施内容配列 */
  contactImplDivArray: { code: number, name: string, div: number }[] = [];

  /** 自動生成履歴用の実施内容 */
  autoGenerationHistory: number[] = [90018, 90019, 90020, 90021, 90022, 90023, 90024];

  /** 適用期間基準区分 */
  applyPeriodCriteriaDivItems = ApplyPeriodCriteriaDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** 月週日区分 */
  monthWeekDayDivItems = MonthWeekDayDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** 適用開始期間基準が「本日」かどうかを返します。 */
  get isApplyStartPeriodToday() {
    return this.contactInitDisplaySettingDtlGrid.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /** 適用終了期間基準が「本日」かどうかを返します。 */
  get isApplyEndPeriodToday() {
    return this.contactInitDisplaySettingDtlGrid.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /**
   * モーダルボタンタイプ
   * @ignore
   */
  ModalButtonType = ModalButtonType;

  /**
   * モーダルボタンカラー
   * @ignore
   */
  ModalButtonColor = ModalButtonColor;

  /** グリッドからの次(下)のフォーカス移動先指定 */
  nextFocusTarget = {
    focus: () => { this.content.focusFooterButton(ModalButton.Cancel); }
  };

  // --------------------------------------------------
  // Life Cycle

  /**
   * コンストラクタ
   * @param service
   */
  constructor(
    private service: ContactInitSettingModalService,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
  ) {
    super();
  }

  ngOnInit() {
    this.showLoading();
    // 初期表示情報取得
    this.service.getLoginUserEmployeeBindModel().pipe(
      mergeMap(loginUserEmployee => {
        this.loginUserEmpCode = loginUserEmployee.employeeCode;
        this.loginUserEmpName = loginUserEmployee.employeeName;
        return ObservableForkJoin(
          this.service.getContactInitDisplaySetting(loginUserEmployee.employeeCode),
          this.service.getContactExecuteItem(loginUserEmployee.organizationCode),
        );
      }),
      finalize(() => this.hideLoading())
    ).subscribe(
      responses => {
        if (responses[0] === null) {
          // 初期表示情報が登録されていなかった場合、isRegistをtrueに設定。呼び元のspaでputするかpostするかの判断に使用する。
          this.contactInitDisplaySettingEx.isRegist = true;

          this.isUseLoginPicFlgGrid = false;
          this.employeeDisableFlgContactList = false;
          this.isUseLoginPicFlgCalendar = false;
          this.employeeDisableFlgCalendar = false;
          this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
          this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
        } else if (!_isEmpty(responses[0])) {
          // 初期表示情報有りの場合
          this.contactInitDisplaySettingEx.isRegist = false;
          this.contactInitDisplaySettingEx = _cloneDeep(responses[0]);
          this.contactInitDisplaySettingDtlGrid = _cloneDeep(responses[0].contactInitDisplaySettingDtlGrid);
          this.contactInitDisplaySettingDtlCalendar = _cloneDeep(responses[0].contactInitDisplaySettingDtlCalendar);
          this.isCalendarSelectContactCheckDivArray = _isEmpty(this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList);
          this.isGridSelectContactCheckDivArray = _isEmpty(this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList);
          this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = this.checkContactGroupDiv(
            this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList,
            this.contactInitDisplaySettingDtlGrid.contactCheckDivList, responses[1]);
          this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = this.checkContactGroupDiv(
            this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList,
            this.contactInitDisplaySettingDtlCalendar.contactCheckDivList, responses[1]);
          ObservableForkJoin(
            this.service.getEmployee(this.contactInitDisplaySettingDtlGrid.picCode),
            this.service.getEmployee(this.contactInitDisplaySettingDtlCalendar.picCode),
          ).subscribe(
            employeeInfo => {
              if (!_isEmpty(employeeInfo[0])) {
                this.contactInitDisplaySettingDtlGrid.picCode = employeeInfo[0].employeeCode;
                this.contactListPicName = employeeInfo[0].employeeName;
                if (this.loginUserEmpCode === employeeInfo[0].employeeCode) {
                  this.isUseLoginPicFlgGrid = true;
                  this.employeeDisableFlgContactList = true;
                } else {
                  this.isUseLoginPicFlgGrid = false;
                  this.employeeDisableFlgContactList = false;
                }
              }
              if (!_isEmpty(employeeInfo[1])) {
                this.contactInitDisplaySettingDtlCalendar.picCode = employeeInfo[1].employeeCode;
                this.calendarPicName = employeeInfo[1].employeeName;
                if (this.loginUserEmpCode === employeeInfo[1].employeeCode) {
                  this.isUseLoginPicFlgCalendar = true;
                  this.employeeDisableFlgCalendar = true;
                } else {
                  this.isUseLoginPicFlgCalendar = false;
                  this.employeeDisableFlgCalendar = false;
                }
              }
            }
          );
        }
        // 自動生成履歴用の実施内容以外をリスト項目とする
        this.contactImplDivArray = [];
        _forEach(responses[1], contactImpl => {
          if (!_includes(this.autoGenerationHistory, contactImpl.code)) {
            this.contactImplDivArray.push(contactImpl);
          }
        });
      }
    );
  }

  // --------------------------------------------------
  // AbstractModal Override

  /**
   * @override
   */
  getResults() {
    this.contactInitDisplaySettingEx.contactInitDisplaySettingDtlCalendar = this.contactInitDisplaySettingDtlCalendar;
    this.contactInitDisplaySettingEx.contactInitDisplaySettingDtlGrid = this.contactInitDisplaySettingDtlGrid;
    return this.contactInitDisplaySettingEx;
  }

  /**
   * @override
   */
  defaultOption() {
    return {
      size: ModalSize.Large,
    };
  }

  /**
   * モーダル終了イベント
   * @param reason　モーダル終了理由
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      if (this.validate()) {
        this.dialogService.confirm('コンタクト初期表示設定を変更します。よろしいですか？').subscribe(
          modalRef => {
            if (modalRef.reason === ModalReason.Done) {
              this.emitDismissEvent(reason);
            }
            modalRef.hide();
          }
        );
      }
    } else {
      this.emitDismissEvent(reason);
    }
  }

  // --------------------------------------------------
  // 内部処理

  /**
   * コンタクトグループ区分チェックボックス押下(カレンダー)
   * @param event
   */
  onChangeCalendarContactCheckDivArray(event: any) {
    const checkItems = _filter(event, item => !_isNil(item));

    if (checkItems.length > 0) {
      this.isCalendarSelectContactCheckDivArray = true;
      const selectedValues = _forEach(event, selectedCheckBox => {
        return selectedCheckBox.value;
      });
      this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = selectedValues;
      // 実施内容区分(チェックボックス)に対応する実施内容(マルチセレクト)を選択状態にする
      const numbers: number[] = [];
      selectedValues.forEach(value => {
        this.contactImplDivArray.forEach(contactImplDiv => {
          if (value === contactImplDiv.div) {
            numbers.push(contactImplDiv.code);
          }
        });
      });
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = numbers;
    } else {
      this.isCalendarSelectContactCheckDivArray = false;
      this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = [];
    }
  }

  /**
   * 実施内容ごとに選択するチェックボックス押下(カレンダー)
   * @param event
   */
  onChangeIsCalendarSelectIndividually(event: boolean) {
    if (!event) {
      this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = [];
    }
  }

  /**
   * コンタクト実施内容セレクトボックス押下(カレンダー)
   * @param event
   */
  onChangeCalendarContactImplDiv(event: any) {
    if (event.checked) {
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList.push(event.item.dropDownValue);
    } else {
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList =
        _filter(this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList,
          code => code !== event.item.dropDownValue
        );
      const findItem = _find(this.contactImplDivArray, item => item.code === event.item.dropDownValue);
      if (findItem) {
        // 実施内容のグループ区分を再設定
        this.contactInitDisplaySettingDtlCalendar.contactCheckDivList
          = _reject(this.contactInitDisplaySettingDtlCalendar.contactCheckDivList, div => div === findItem.div);
      }
    }
  }

  /**
   * コンタクトグループ区分チェックボックス押下(一覧)
   * @param event
   */
  onChangeGridContactCheckDivArray(event: any) {
    const checkItems = _filter(event, item => !_isNil(item));

    if (checkItems.length > 0) {
      this.isGridSelectContactCheckDivArray = true;
      const selectedValues = _forEach(event, selectedCheckBox => {
        return selectedCheckBox.value;
      });
      this.contactInitDisplaySettingDtlGrid.contactCheckDivList = selectedValues;
      // 実施内容区分(チェックボックス)に対応する実施内容(マルチセレクト)を選択状態にする
      const numbers: number[] = [];
      selectedValues.forEach(value => {
        this.contactImplDivArray.forEach(contactImplDiv => {
          if (value === contactImplDiv.div) {
            numbers.push(contactImplDiv.code);
          }
        });
      });
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = numbers;
    } else {
      this.isGridSelectContactCheckDivArray = false;
      this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = [];
    }
  }

  /**
   * 実施内容ごとに選択するチェックボックス押下(一覧)
   * @param event
   */
  onChangeIsGridSelectIndividually(event: boolean) {
    if (!event) {
      this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = [];
    }
  }

  /**
   * コンタクト実施内容セレクトボックス押下(一覧)
   * @param event
   */
  onChangeGridContactImplDiv(event: any) {
    if (event.checked) {
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList.push(event.item.dropDownValue);
    } else {
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList =
        _filter(this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList,
          code => code !== event.item.dropDownValue
        );
      const findItem = _find(this.contactImplDivArray, item => item.code === event.item.dropDownValue);
      if (findItem) {
        // 実施内容のグループ区分を再設定
        this.contactInitDisplaySettingDtlGrid.contactCheckDivList
          = _reject(this.contactInitDisplaySettingDtlGrid.contactCheckDivList, div => div === findItem.div);
      }
    }
  }

  /**
   * コンタクトグループ区分に紐づくコンタクト実施内容のチェック処理
   * @param targetItemCodeList コンタクト実施内容コード配列
   * @param contactCheckDivList コンタクトグループ区分
   * @param contactExecuteItemList コンタクト実施内容配列
   * @returns コンタクト実施内容コード配列
   */
  private checkContactGroupDiv(
    targetItemCodeList: number[],
    contactCheckDivList: number[],
    contactExecuteItemList: { code: number; name: string; div: number; }[]
  ): number[] {
    if (contactCheckDivList.length === 0) {
      return targetItemCodeList;
    }

    // コンタクトグループ区分に紐づくコンタクト実施内容を抽出する
    const newItemCodeList = _filter(contactExecuteItemList, item =>
      _includes(contactCheckDivList, item.div)
    ).map(item => item.code);

    // 既存チェック分をマージ
    _forEach(targetItemCodeList, code => {
      if (!_includes(newItemCodeList, code)) {
        newItemCodeList.push(code);
      }
    });

    return newItemCodeList;
  }

  /**
   * 担当者ガイドボタン押下
   * @param propertyId　カレンダーまたは一覧
   * @param btnEl　フォーカスターゲット
   */
  onClickEmployeeGuide(propertyId: string, btnEl) {

    this.modalService.show(EmployeeGuideComponent).pipe().subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          const result = <IEmployee>modalRef.getResults();
          if (!_isEmpty(result)) {
            if (propertyId === 'calendar') {
              this.contactInitDisplaySettingDtlCalendar.picCode = result.employeeCode;
              this.calendarPicName = result.employeeName;
              if (this.loginUserEmpCode === result.employeeCode) {
                this.isUseLoginPicFlgCalendar = true;
                this.employeeDisableFlgCalendar = true;
              } else {
                this.isUseLoginPicFlgCalendar = false;
                this.employeeDisableFlgCalendar = false;
              }
            } else if (propertyId === 'contactList') {
              this.contactInitDisplaySettingDtlGrid.picCode = result.employeeCode;
              this.contactListPicName = result.employeeName;
              if (this.loginUserEmpCode === result.employeeCode) {
                this.isUseLoginPicFlgGrid = true;
                this.employeeDisableFlgContactList = true;
              } else {
                this.isUseLoginPicFlgGrid = false;
                this.employeeDisableFlgContactList = false;
              }
            }
          }
        }
        modalRef.hide(btnEl);
      }
    );
  }

  /**
   * 従業員ガイド制御処理
   *
   * @param checked　チェック状態
   * @param propertyId　カレンダーまたは一覧
   */
  onChangeEmployeeFlg(checked: boolean, propertyId: string) {
    if (propertyId === 'calendar') {
      if (checked === false) {
        this.employeeDisableFlgCalendar = false;
      } else {
        this.employeeDisableFlgCalendar = true;
        this.contactInitDisplaySettingDtlCalendar.picCode = this.loginUserEmpCode;
        this.calendarPicName = this.loginUserEmpName;
      }
    } else if (propertyId === 'contactList') {
      if (checked === false) {
        this.employeeDisableFlgContactList = false;
      } else {
        this.employeeDisableFlgContactList = true;
        this.contactInitDisplaySettingDtlGrid.picCode = this.loginUserEmpCode;
        this.contactListPicName = this.loginUserEmpName;
      }
    }
  }

  /**
   * 従業員コード手入力
   * @param propertyId　カレンダーまたは一覧
   * @param value　入力値
   */
  changePicCode(propertyId: string, value: string) {
    this.service.getEmployee(value).subscribe(
      emp => {
        if (!_isEmpty(emp)) {
          if (propertyId === 'calendar') {
            this.contactInitDisplaySettingDtlCalendar.picCode = emp.employeeCode;
            this.calendarPicName = emp.employeeName;
            if (this.loginUserEmpCode === emp.employeeCode) {
              this.isUseLoginPicFlgCalendar = true;
              this.employeeDisableFlgCalendar = true;
            } else {
              this.isUseLoginPicFlgCalendar = false;
              this.employeeDisableFlgCalendar = false;
            }
          } else if (propertyId === 'contactList') {
            this.contactInitDisplaySettingDtlGrid.picCode = emp.employeeCode;
            this.contactListPicName = emp.employeeName;
            if (this.loginUserEmpCode === emp.employeeCode) {
              this.isUseLoginPicFlgGrid = true;
              this.employeeDisableFlgContactList = true;
            } else {
              this.isUseLoginPicFlgGrid = false;
              this.employeeDisableFlgContactList = false;
            }
          }
        } else {
          if (propertyId === 'calendar') {
            this.contactInitDisplaySettingDtlCalendar.picCode = '';
            this.calendarPicName = '';
          } else if (propertyId === 'contactList') {
            this.contactInitDisplaySettingDtlGrid.picCode = '';
            this.contactListPicName = '';
          }
        }
      }
    );
  }

  /**
   * 整合性チェック処理
   */
  private validate() {
    if (!this.contactInitDisplaySettingDtlGrid.organizationCode || !this.contactInitDisplaySettingDtlCalendar.organizationCode) {
      this.dialogService.warn('組織を入力してください。').subscribe(
        errorModalRef => {
          errorModalRef.hide();
        }
      );
    } else if (!this.validateStartInputValues() || !this.validateEndInputValues()) {
      this.dialogService.warn('対象期間（実施予定日）を入力してください。').subscribe(modalRef => modalRef.hide());
    } else {
      // 対象開始日
      const start = this.getApplyPeriodDate(
        this.contactInitDisplaySettingDtlGrid.applyStartPeriodCriteriaDiv,
        this.contactInitDisplaySettingDtlGrid.monthWeekDayDiv,
        this.contactInitDisplaySettingDtlGrid.applyStartNum
      );
      // 対象終了日
      const end = this.getApplyPeriodDate(
        this.contactInitDisplaySettingDtlGrid.applyEndPeriodCriteriaDiv,
        this.contactInitDisplaySettingDtlGrid.monthWeekDayDiv,
        this.contactInitDisplaySettingDtlGrid.applyEndNum
      );

      // 開始期間が終了期間よりも後の日付になっていた場合
      if (start > end) {
        this.dialogService.warn('対象期間が不正です。入力内容を確認してください。').subscribe(modalRef => modalRef.hide());
        // 期間が一年未満かチェック
      } else if (start.add(12, 'M') < end) {
        this.dialogService.warn('対象期間は1年以内で指定してください。').subscribe(modalRef => modalRef.hide());
      } else {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間（開始）入力チェック
   * applyStartPeriodCriteriaDivがTodayの場合、常にtrueを返却。
   * applyStartPeriodCriteriaDivがToday以外の場合、applyStartNumが1以上であればtrueを返却。
   */
  validateStartInputValues(): boolean {
    if (this.contactInitDisplaySettingDtlGrid.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
      return true;
    } else {
      if (this.contactInitDisplaySettingDtlGrid.applyStartNum) {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間（終了）入力チェック
   * applyEndPeriodCriteriaDivがTodayの場合、常にtrueを返却。
   * applyEndPeriodCriteriaDivがToday以外の場合、applyEndNumが1以上であればtrueを返却。
   */
  validateEndInputValues(): boolean {
    if (this.contactInitDisplaySettingDtlGrid.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
      return true;
    } else {
      if (this.contactInitDisplaySettingDtlGrid.applyEndNum) {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間日の取得処理
   * @param applyPeriodCriteriaDiv 適用期間基準区分
   * @param monthWeekDayDiv 月週日区分
   * @param applyNum 適用値
   * @returns
   */
  private getApplyPeriodDate(applyPeriodCriteriaDiv: string, monthWeekDayDiv: string, applyNum: number) {
    let date = moment().clone();

    if (applyPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.PastFromToday) {
      switch (monthWeekDayDiv) {
        case MonthWeekDayDiv.Day:
          date = date.subtract(applyNum, 'days');
          break;
        case MonthWeekDayDiv.Week:
          date = date.subtract(applyNum, 'weeks');
          break;
        case MonthWeekDayDiv.Month:
          date = date.subtract(applyNum, 'M');
          break;
      }
    } else if (applyPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.FutureFromToday) {
      switch (monthWeekDayDiv) {
        case MonthWeekDayDiv.Day:
          date = date.add(applyNum, 'days');
          break;
        case MonthWeekDayDiv.Week:
          date = date.add(applyNum, 'weeks');
          break;
        case MonthWeekDayDiv.Month:
          date = date.add(applyNum, 'M');
          break;
      }
    }

    return date;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/contact-init-setting/contact-init-setting-modal.component.ts